import { inject, Injectable } from '@angular/core';
import { COMMON_VALIDATION, EMAIL, INDIVIDUAL_NAME, Method, NRIC, PersonnelRelationship } from '@core/data-access';
import { get18YearsAgo } from '@core/util';
import { KeyPersonnelRelationshipToApplicants } from '@portal/data-access';
import { PortalFieldConfig, uppercaseParser } from '@portal/form/data-access';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, EMPTY, filter, startWith, tap } from 'rxjs';
import { PERSONNEL_OTHER } from '../const/app.const';
import { BeneficialOwnerModelKeys } from '../const/form-field-key.const';
import { AuthorizedPersonnelOption, BeneficialOwnerOption } from '../type';
import { constraintRelationshipOptions } from '../util';
import { MobileFormConfigService } from './mobile-form-config.service';
import { FormGroup } from '@angular/forms';
import { BeneficialOwnerModel } from '../model';
import { ApplicationStore, FinancialRequiredStore } from '../store';

@Injectable({ providedIn: 'root' })
export class BeneficialOwnerFormConfigService {
  private readonly mobileFormConfigService = inject(MobileFormConfigService);
  private readonly financialRequiredStore = inject(FinancialRequiredStore);
  private readonly applicationStore = inject(ApplicationStore);
  readonly portalBeneficialOwnerOptionsMap$ = new BehaviorSubject<Map<string, BeneficialOwnerOption>>(new Map());
  readonly portalBeneficialOwnerOptions$ = new BehaviorSubject<BeneficialOwnerOption[]>([]);

  initBeneficialOwnerFormFields(): PortalFieldConfig[] {
    return [
      {
        key: BeneficialOwnerModelKeys.metadata,
        className: 'hidden',
        fieldGroup: [
          {
            key: 'uuid',
            // type: 'input',
            // props: { label: 'metadata.uuid', disabled: true },
          },
        ],
      },
      {
        key: BeneficialOwnerModelKeys.keyPersonnelId,
        // type: 'input',
        // props: { label: 'KeyPersonnelId', disabled: true },
      },
      {
        key: BeneficialOwnerModelKeys.method,
        // type: 'input',
        // props: { label: 'Method', disabled: true },
      },
      {
        key: BeneficialOwnerModelKeys.personnel_uuid,
        type: 'mat-select',
        expressions: { 'props.disabled': field => this.notOtherOption(field) },
        resetOnHide: false,
        hooks: {
          onInit: ({ form, formControl, parent, model }: PortalFieldConfig) => {
            const formControlChanges$ = formControl?.valueChanges.pipe(startWith(formControl?.value), distinctUntilChanged()) || EMPTY;
            const portalPersonnelOptionsMap$ = this.financialRequiredStore.portalPersonnelOptionsMap$;

            const getUuidSelected = (): string | null => {
              const uuid = formControl?.getRawValue();
              if (uuid === PERSONNEL_OTHER || typeof uuid !== 'string' || !model?.[BeneficialOwnerModelKeys.personnel_uuid]) return null;
              return uuid;
            };

            const transformBOOptionMap = (
              optionsMap: Map<string, AuthorizedPersonnelOption>,
              updateFn?: (us: BeneficialOwnerModel) => void,
            ) => {
              const options = Array.from(optionsMap.values())
                .filter(v => v.value !== PERSONNEL_OTHER)
                .map<BeneficialOwnerOption>(v => {
                  const { personnel, ...bo } = v;
                  const beneficialOwner = BeneficialOwnerModel.fromJson(personnel);
                  const uuid = personnel?.uuid === 'PERSONNEL_OTHER' ? personnel?.metadata?.uuid : personnel?.uuid;
                  beneficialOwner.personnel_uuid = uuid || null;
                  beneficialOwner.method = Method.populate;

                  if (typeof updateFn === 'function') {
                    updateFn(beneficialOwner);
                  }

                  return { ...bo, beneficialOwner };
                });
              return {
                map: new Map<string, BeneficialOwnerOption>(options.map(o => [o.value, o])), // unique by value
                options,
              };
            };

            const removeFormControl = (option: BeneficialOwnerOption | undefined) => {
              const uuid = getUuidSelected();
              if (!uuid) return;

              // const option = this.portalBeneficialOwnerOptionsMap$.getValue().get(uuid);
              const parentProps = parent?.parent?.props;
              const beneficialOwners: BeneficialOwnerModel[] = parent?.parent?.model ?? [];

              if (!option && model?.[BeneficialOwnerModelKeys.personnel_uuid] !== PERSONNEL_OTHER) {
                const indexToRemove = beneficialOwners.findIndex(v => v[BeneficialOwnerModelKeys.personnel_uuid] === formControl?.value);
                if (indexToRemove !== -1) {
                  parentProps?.['remove']?.(indexToRemove);
                }
              }
            };

            const getPortalOptions = () => {
              const portalPersonnelOptionsMap = portalPersonnelOptionsMap$.getValue();
              const appPersonnelOptionsMap = this.applicationStore.appPersonnelOptionsMap$.getValue();

              const corppassOptionsMap = new Map(
                Array.from(appPersonnelOptionsMap.entries()).filter(([key, value]) => {
                  return !portalPersonnelOptionsMap.has(key) && value.personnel?.method === Method.corppass;
                }),
              );

              return {
                selected: {
                  map: portalPersonnelOptionsMap,
                  options: portalPersonnelOptionsMap.values(),
                },
                corppass: {
                  map: corppassOptionsMap,
                  options: Array.from(corppassOptionsMap.values()).filter(v => v.value !== PERSONNEL_OTHER),
                },
              };
            };

            const mergePortalPersonnelOptionsMapToBOOptionsMap = () => {
              const portalOptions = getPortalOptions();
              const portalPersonnelSelectedOptions = transformBOOptionMap(portalOptions.selected.map, us => {
                us.method = Method.populate;
              }).options;
              const corppassPersonnelOptions = transformBOOptionMap(portalOptions.corppass.map, us => {
                us.method = Method.corppass;
              }).options;

              const appBOOptionsMap = this.applicationStore.appBeneficialOwnerOptionsMap$.getValue();
              // const portalPersonnelOptions = transformBOOptionMap(portalPersonnelOptionsMap$.getValue()).options;

              const portalBOOptionsMap = new Map<string, BeneficialOwnerOption>(
                [...portalPersonnelSelectedOptions, ...corppassPersonnelOptions, ...appBOOptionsMap.values()].map(o => [o.value, o]),
              ); // unique by value
              this.portalBeneficialOwnerOptionsMap$.next(portalBOOptionsMap);

              return portalBOOptionsMap; // NOTE: to keep older version of the dataflow
            };

            const populateForm = (option?: BeneficialOwnerOption): void => {
              if (option?.beneficialOwner && form instanceof FormGroup) {
                /**
                 * Check conditions to hide fields based on the selected personnel
                 */
                const relationshipToApplicantOther = parent?.fieldGroup?.find(
                  f => f.key === BeneficialOwnerModelKeys.relationshipToApplicantOther,
                );
                if (relationshipToApplicantOther) {
                  relationshipToApplicantOther.hide = !option.beneficialOwner.relationshipToApplicant.includes(PersonnelRelationship.OTHER);
                }

                const dualNationalityId = parent?.fieldGroup?.find(f => f.key === BeneficialOwnerModelKeys.dualNationalityId);
                if (dualNationalityId) {
                  dualNationalityId.hide = !option.beneficialOwner.dualNationality;
                }

                form.patchValue(option.beneficialOwner);
              }
            };

            const populateFormBasedOnBOOptionMap = (BOOptionMap: Map<string, BeneficialOwnerOption>) => {
              const uuid = getUuidSelected();
              if (!uuid) return;

              const option = BOOptionMap.get(uuid);
              populateForm(option);
            };

            return combineLatest([
              formControlChanges$.pipe(
                filter(() => !!getUuidSelected()),
                tap(() => {
                  const uuid = getUuidSelected();
                  if (!uuid || !(form instanceof FormGroup)) return;

                  const portalOptions = getPortalOptions();
                  const corppassPersonnelMap = transformBOOptionMap(portalOptions.corppass.map, us => {
                    us.method = Method.corppass;
                  }).map;

                  const existingCorppassPersonnel = corppassPersonnelMap.get(uuid);
                  if (existingCorppassPersonnel?.beneficialOwner) {
                    const relationshipToApplicant = form.getRawValue()?.[BeneficialOwnerModelKeys.relationshipToApplicant];
                    const relationshipToApplicantOther = form.getRawValue()?.[BeneficialOwnerModelKeys.relationshipToApplicantOther];
                    if (relationshipToApplicant) {
                      form.patchValue({
                        ...existingCorppassPersonnel.beneficialOwner,
                        relationshipToApplicant: relationshipToApplicant,
                        relationshipToApplicantOther: relationshipToApplicantOther ?? null,
                      });
                      return;
                    }

                    form.patchValue(existingCorppassPersonnel.beneficialOwner);
                    return;
                  }
                  const portalBOOptionsMap = this.portalBeneficialOwnerOptionsMap$.getValue();
                  populateFormBasedOnBOOptionMap(portalBOOptionsMap);
                }),
              ),
              portalPersonnelOptionsMap$.pipe(
                debounceTime(300),
                tap(portalPersonnelOptionsMap => {
                  const uuid = getUuidSelected();
                  if (!uuid || !(form instanceof FormGroup)) return;

                  const portalOptions = getPortalOptions();
                  const portalPersonnelShareholderSelectedMap = transformBOOptionMap(portalOptions.selected.map, us => {
                    us.method = Method.populate;
                  }).map;

                  const existingPortalOption = portalPersonnelShareholderSelectedMap.get(uuid);
                  const method = form?.getRawValue()?.[BeneficialOwnerModelKeys.method];
                  if (method === Method.populate) {
                    removeFormControl(existingPortalOption);
                  }

                  const portalBOOptionsMap = transformBOOptionMap(portalPersonnelOptionsMap).map;
                  populateFormBasedOnBOOptionMap(portalBOOptionsMap);
                }),
                tap(() => {
                  mergePortalPersonnelOptionsMapToBOOptionsMap();
                }),
                tap(() => {
                  const uuid = getUuidSelected();
                  if (!uuid || !(form instanceof FormGroup)) return;

                  const option = this.portalBeneficialOwnerOptionsMap$.getValue().get(uuid);
                  if (option) removeFormControl(option);
                }),
              ),
            ]);
          },
        },
        props: {
          label: 'Select Personnel',
          placeholder: 'Please select',
          options: this.portalBeneficialOwnerOptions$,
          required: true,
        },
      },
      {
        key: BeneficialOwnerModelKeys.name,
        type: 'input',
        expressions: { hide: field => this.disableControl(field) },
        resetOnHide: false,
        props: {
          label: 'Name as per NRIC/Passport',
          placeholder: 'Please enter input',
          required: true,
          maxLength: COMMON_VALIDATION.MAX_LENGTH,
          maskConfig: {
            mask: 'a*',
            config: { patterns: { a: { pattern: INDIVIDUAL_NAME.PATTERN } }, dropSpecialCharacters: false },
          },
        },
      },
      {
        key: BeneficialOwnerModelKeys.nricNumber,
        type: 'input',
        expressions: { 'props.disabled': field => this.disableControl(field) },
        resetOnHide: false,
        props: {
          label: 'NRIC/Passport No.',
          placeholder: 'e.g. S1234567D',
          minLength: NRIC.MIN_LENGTH,
          maxLength: NRIC.MAX_LENGTH,
          required: true,
          maskConfig: { mask: 'A*' },
        },
        // validators: { validation: ['nric'] },
        parsers: [uppercaseParser],
        // hooks: { onInit: field => this.disableControlBasedOnStatusChange(field) },
      },
      {
        key: BeneficialOwnerModelKeys.dateOfBirthOnly,
        type: 'datepicker',
        expressions: { hide: field => this.disableControl(field) },
        resetOnHide: false,
        validators: { validation: ['dateRange'] },
        props: {
          label: 'Date of birth',
          placeholder: 'Please enter input',
          // minDate: getDate100YearsAgo(),
          // maxDate: getEndOfToday(),
          maxDate: get18YearsAgo(),
        },
      },
      {
        expressions: { hide: field => this.disableControl(field) },
        resetOnHide: false,
        fieldGroup: this.mobileFormConfigService.initMobileFieldConfig({
          label: 'Mobile Number',
          key: {
            areaCode: BeneficialOwnerModelKeys.contactAreaCode,
            number: BeneficialOwnerModelKeys.contactNumber,
          },
        }),
      },
      {
        key: BeneficialOwnerModelKeys.email,
        type: 'input',
        resetOnHide: false,
        expressions: { hide: field => this.disableControl(field) },
        props: {
          label: 'Email Address',
          placeholder: 'Please enter input',
          required: true,
          maxLength: EMAIL.MAX_LENGTH,
        },
        validators: {
          validation: [{ name: 'email-custom', options: { keyName: BeneficialOwnerModelKeys.email } }],
        },
        // parsers: [lowercaseParser],
      },
      {
        key: BeneficialOwnerModelKeys.nationalityId,
        type: '#nationality',
        props: { required: true },
        expressions: { 'props.disabled': field => this.disableControl(field) },
      },
      {
        key: BeneficialOwnerModelKeys.dualNationality,
        type: '#yesNo',
        defaultValue: false,
        expressions: { 'props.disabled': field => this.disableControl(field) },
        props: { label: 'Other Nationalities' },
      },
      {
        key: BeneficialOwnerModelKeys.dualNationalityId,
        type: '#nationality',
        props: { label: 'Other Nationalities (Please indicate)' },
        expressions: {
          hide: field => !field.model?.[BeneficialOwnerModelKeys.dualNationality],
          'props.required': ({ model }) => model?.[BeneficialOwnerModelKeys.dualNationality],
          'props.disabled': field => this.disableControl(field),
        },
        hooks: {
          onInit: field => {
            return field?.form?.get(BeneficialOwnerModelKeys.personnel_uuid)?.valueChanges.pipe(
              distinctUntilChanged(),
              filter(value => value !== PERSONNEL_OTHER && !!value && typeof value === 'string'),
              tap(uuid => {
                const portalBOOptionsMap = this.portalBeneficialOwnerOptionsMap$.getValue();
                const option = portalBOOptionsMap.get(uuid);
                if (typeof option?.beneficialOwner?.dualNationalityId === 'number') {
                  field?.formControl?.setValue(option?.beneficialOwner?.dualNationalityId);
                }
              }),
            );
          },
        },
      },
      {
        key: BeneficialOwnerModelKeys.relationshipToApplicant,
        type: 'mat-select',
        resetOnHide: false,
        className: 'col-start-1',
        expressions: { 'props.disabled': field => this.methodPopulate(field) },
        props: {
          label: 'Relationship to Applicant',
          placeholder: 'Please select',
          multiple: true,
          required: true,
          options: Array.from(KeyPersonnelRelationshipToApplicants),
        },
        hooks: {
          onInit: field => constraintRelationshipOptions({ field }),
        },
      },
      {
        key: BeneficialOwnerModelKeys.relationshipToApplicantOther,
        type: 'input',
        resetOnHide: false,
        props: {
          label: 'Relationship to Applicant (Others)',
          placeholder: 'Please enter input',
          required: true,
          maxLength: COMMON_VALIDATION.MAX_LENGTH,
        },
        hooks: { onInit: field => constraintRelationshipOptions({ field }) },
        expressions: {
          hide: ({ model }) => {
            const relationshipValue = model?.[BeneficialOwnerModelKeys.relationshipToApplicant];
            return relationshipValue && !relationshipValue.includes('OTHER');
          },
          'props.disabled': field => field.model?.[BeneficialOwnerModelKeys.method] === Method.populate,
        },
      },
    ];
  }

  private methodPopulate({ model }: PortalFieldConfig): boolean {
    return model?.[BeneficialOwnerModelKeys.method] === Method.populate;
  }

  private disableControl({ model }: PortalFieldConfig): boolean {
    return model?.[BeneficialOwnerModelKeys.method] === Method.populate || model?.[BeneficialOwnerModelKeys.method] === Method.corppass;
  }

  private notOtherOption({ model }: PortalFieldConfig): boolean {
    return (
      typeof model?.[BeneficialOwnerModelKeys.personnel_uuid] === 'string' &&
      model?.[BeneficialOwnerModelKeys.personnel_uuid] !== PERSONNEL_OTHER
    );
  }

  // private disableControl({ model, formControl, options }: PortalFieldConfig): void {
  //   const existed = this.methodPopulate({ model });
  //   const disabled = existed || options?.formState?.disabled;

  //   if (disabled) {
  //     formControl?.disable({ onlySelf: true, emitEvent: false });
  //   } else {
  //     formControl?.enable({ onlySelf: true, emitEvent: false });
  //   }
  // }
}
